import React, { useState, useEffect, useCallback } from 'react';
import { 
  Cloud, 
  CloudRain, 
  CloudSnow, 
  Sun, 
  CloudSun,
  CloudLightning,
  Wind,
  Droplets,
  Sunrise,
  Sunset,
  AlertTriangle,
  Lightbulb,
  Moon,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Weather icon mapper
const getWeatherIcon = (condition, size = 24) => {
  const iconClass = "text-current";
  switch (condition?.toLowerCase()) {
    case 'clear':
      return <Sun size={size} className={cn(iconClass, "text-yellow-500")} />;
    case 'clouds':
    case 'cloudy':
      return <Cloud size={size} className={cn(iconClass, "text-slate-400")} />;
    case 'rain':
    case 'drizzle':
      return <CloudRain size={size} className={cn(iconClass, "text-blue-500")} />;
    case 'snow':
      return <CloudSnow size={size} className={cn(iconClass, "text-blue-200")} />;
    case 'thunderstorm':
      return <CloudLightning size={size} className={cn(iconClass, "text-purple-500")} />;
    default:
      return <CloudSun size={size} className={cn(iconClass, "text-slate-400")} />;
  }
};

export default function WeatherWidget({ compact = false }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchWeather = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/weather/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch weather');
      }

      const data = await response.json();
      setWeather(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Weather fetch error:', err);
      setError('Unable to load weather');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeather();
    // Refresh every 10 minutes
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWeather]);

  // Count active alerts
  const alertCount = weather ? [
    weather.alerts?.rain_coming,
    weather.alerts?.getting_dark?.active,
    weather.alerts?.closing_time?.active
  ].filter(Boolean).length : 0;

  if (loading) {
    return (
      <div className={cn(
        "bg-gradient-to-r from-blue-50 to-sky-50 rounded-lg p-3 animate-pulse",
        compact ? "w-fit" : "w-full"
      )}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-200 rounded-full" />
          <div className="space-y-1">
            <div className="h-4 w-16 bg-slate-200 rounded" />
            <div className="h-3 w-24 bg-slate-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className={cn(
        "bg-slate-100 rounded-lg p-3 text-slate-500 text-sm",
        compact ? "w-fit" : "w-full"
      )}>
        <div className="flex items-center gap-2">
          <Cloud size={20} className="text-slate-400" />
          <span>Weather unavailable</span>
        </div>
      </div>
    );
  }

  const { current, alerts, sun, forecast, store, is_mock } = weather;

  // Compact view for header/sidebar
  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-lg transition-all",
            "hover:bg-slate-100",
            alertCount > 0 ? "bg-amber-50 border border-amber-200" : "bg-blue-50/50"
          )}
          data-testid="weather-widget-compact"
        >
          {getWeatherIcon(current.condition, 20)}
          <span className="font-medium text-slate-700">{current.temperature}°C</span>
          {alertCount > 0 && (
            <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-amber-500 rounded-full">
              {alertCount}
            </span>
          )}
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {/* Dropdown panel */}
        {expanded && (
          <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
            <WeatherDetailPanel 
              weather={weather} 
              onRefresh={fetchWeather}
              lastUpdated={lastUpdated}
            />
          </div>
        )}
      </div>
    );
  }

  // Full widget view
  return (
    <div 
      className="bg-gradient-to-br from-blue-500 to-sky-600 rounded-xl p-4 text-white shadow-lg"
      data-testid="weather-widget-full"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">{store}</h3>
          <p className="text-blue-100 text-sm">{current.description}</p>
        </div>
        <button 
          onClick={fetchWeather}
          className="p-2 hover:bg-white/20 rounded-full transition-colors"
          title="Refresh weather"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        {getWeatherIcon(current.condition, 48)}
        <div>
          <span className="text-4xl font-bold">{current.temperature}°</span>
          <span className="text-blue-200 ml-2">Feels like {current.feels_like}°</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <Droplets size={16} className="text-blue-200" />
          <span>{current.humidity}%</span>
        </div>
        <div className="flex items-center gap-2">
          <Wind size={16} className="text-blue-200" />
          <span>{current.wind_speed} km/h</span>
        </div>
        <div className="flex items-center gap-2">
          <Sunset size={16} className="text-blue-200" />
          <span>{sun?.sunset || '--:--'}</span>
        </div>
      </div>

      {/* Alerts */}
      {alertCount > 0 && (
        <div className="space-y-2 mb-4">
          {alerts.rain_coming && (
            <div className="flex items-center gap-2 bg-amber-500/90 rounded-lg px-3 py-2 text-sm">
              <CloudRain size={18} />
              <span className="font-medium">{alerts.rain_coming.message}</span>
            </div>
          )}
          {alerts.getting_dark?.active && (
            <div className="flex items-center gap-2 bg-orange-500/90 rounded-lg px-3 py-2 text-sm">
              <Lightbulb size={18} />
              <span className="font-medium">{alerts.getting_dark.message}</span>
            </div>
          )}
          {alerts.closing_time?.active && (
            <div className="flex items-center gap-2 bg-purple-500/90 rounded-lg px-3 py-2 text-sm">
              <Moon size={18} />
              <span className="font-medium">{alerts.closing_time.message}</span>
            </div>
          )}
        </div>
      )}

      {/* Forecast */}
      {forecast && forecast.length > 0 && (
        <div className="border-t border-white/20 pt-3">
          <p className="text-xs text-blue-200 mb-2">Next hours</p>
          <div className="flex gap-3 overflow-x-auto">
            {forecast.slice(0, 4).map((fc, idx) => (
              <div key={idx} className="flex flex-col items-center text-xs min-w-[50px]">
                <span className="text-blue-200">{fc.time}</span>
                {getWeatherIcon(fc.condition, 20)}
                <span className="font-medium">{fc.temp}°</span>
                {fc.rain_chance > 30 && (
                  <span className="text-blue-200">{fc.rain_chance}%</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {is_mock && (
        <p className="text-xs text-blue-200 mt-2 opacity-70">Demo data - Add API key for live weather</p>
      )}
    </div>
  );
}

// Detailed panel for dropdown
function WeatherDetailPanel({ weather, onRefresh, lastUpdated }) {
  const { current, alerts, sun, forecast, store, is_mock } = weather;
  const alertCount = [
    alerts?.rain_coming,
    alerts?.getting_dark?.active,
    alerts?.closing_time?.active
  ].filter(Boolean).length;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-semibold text-slate-800">{store}</h4>
          <p className="text-xs text-slate-500">{current.description}</p>
        </div>
        <button 
          onClick={onRefresh}
          className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-slate-500"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Current conditions */}
      <div className="flex items-center gap-3 mb-4">
        {getWeatherIcon(current.condition, 36)}
        <div>
          <span className="text-3xl font-bold text-slate-800">{current.temperature}°</span>
          <span className="text-slate-500 ml-1">Feels {current.feels_like}°</span>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
        <div className="flex flex-col items-center p-2 bg-slate-50 rounded-lg">
          <Droplets size={14} className="text-blue-500 mb-1" />
          <span className="text-slate-600">{current.humidity}%</span>
          <span className="text-slate-400">Humidity</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-slate-50 rounded-lg">
          <Wind size={14} className="text-slate-500 mb-1" />
          <span className="text-slate-600">{current.wind_speed} km/h</span>
          <span className="text-slate-400">Wind</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-slate-50 rounded-lg">
          <Sunset size={14} className="text-orange-500 mb-1" />
          <span className="text-slate-600">{sun?.sunset || '--:--'}</span>
          <span className="text-slate-400">Sunset</span>
        </div>
      </div>

      {/* Alerts */}
      {alertCount > 0 && (
        <div className="space-y-2 mb-4">
          {alerts.rain_coming && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <CloudRain size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Rain Alert</p>
                <p className="text-xs text-amber-600">{alerts.rain_coming.message}</p>
              </div>
            </div>
          )}
          {alerts.getting_dark?.active && (
            <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              <Lightbulb size={16} className="text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-800">Turn On Lights</p>
                <p className="text-xs text-orange-600">{alerts.getting_dark.message}</p>
              </div>
            </div>
          )}
          {alerts.closing_time?.active && (
            <div className="flex items-start gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
              <Moon size={16} className="text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-purple-800">Closing Time</p>
                <p className="text-xs text-purple-600">{alerts.closing_time.message}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mini forecast */}
      {forecast && forecast.length > 0 && (
        <div className="border-t border-slate-100 pt-3">
          <p className="text-xs text-slate-500 mb-2 font-medium">Upcoming</p>
          <div className="flex justify-between">
            {forecast.slice(0, 4).map((fc, idx) => (
              <div key={idx} className="flex flex-col items-center text-xs">
                <span className="text-slate-400">{fc.time}</span>
                {getWeatherIcon(fc.condition, 18)}
                <span className="font-medium text-slate-700">{fc.temp}°</span>
                {fc.rain_chance > 20 && (
                  <span className="text-blue-500 flex items-center gap-0.5">
                    <Droplets size={10} />{fc.rain_chance}%
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
        <span>
          {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
        </span>
        {is_mock && <span className="text-amber-500">Demo data</span>}
      </div>
    </div>
  );
}

// Export a mini version for notification bar
export function WeatherMini() {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${API_URL}/api/weather/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          setWeather(await response.json());
        }
      } catch (err) {
        console.error('Weather fetch error:', err);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!weather) return null;

  const alertCount = [
    weather.alerts?.rain_coming,
    weather.alerts?.getting_dark?.active,
    weather.alerts?.closing_time?.active
  ].filter(Boolean).length;

  return (
    <div className="flex items-center gap-1.5 text-sm" data-testid="weather-mini">
      {getWeatherIcon(weather.current?.condition, 16)}
      <span className="font-medium">{weather.current?.temperature}°</span>
      {alertCount > 0 && (
        <AlertTriangle size={14} className="text-amber-500" />
      )}
    </div>
  );
}
